import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Home as HomeIcon,
  Architecture as ArchitectureIcon,
  Lightbulb as LightbulbIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

// Coverage areas
const currentCoverage = ['Somerville, MA', 'Cambridge, MA', 'Lexington, MA'];
const comingSoon = [
  'Boston', 'Brookline', 'Newton', 'Arlington', 'Belmont', 'Watertown',
  'Medford', 'Malden', 'Everett', 'Chelsea', 'Waltham', 'Quincy'
];

const values = [
  {
    icon: <ArchitectureIcon />,
    title: 'Accuracy First',
    description: 'We use real data from authenticated sources, never estimates or approximations.',
  },
  {
    icon: <LightbulbIcon />,
    title: 'Innovation',
    description: 'Continuously improving our algorithms and adding new features based on user feedback.',
  },
  {
    icon: <PeopleIcon />,
    title: 'User-Centric',
    description: 'Built by real estate professionals, for real estate professionals.',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Transparency',
    description: 'Clear pricing, no hidden fees, and open about our data sources and methods.',
  },
];

const milestones = [
  { year: '2023', event: 'QuickBuildr.ai founded with mission to democratize property analysis' },
  { year: '2024', event: 'Integrated with Massachusetts GIS systems and launched beta' },
  { year: '2024', event: 'Reached 1,000+ properties analyzed across Massachusetts' },
  { year: '2025', event: 'Launched AI-powered recommendations and enterprise features' },
];

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" color="primary" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 600 }}>
            QuickBuildr.ai
          </Typography>
          {isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              fontWeight={700}
              color="primary.main"
              gutterBottom
            >
              About QuickBuildr.ai
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
            >
              Making property development analysis accessible, accurate, and actionable
            </Typography>
          </motion.div>

          {/* Mission Section */}
          <Box sx={{ my: 8, p: 6, bgcolor: 'white', borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Our Mission
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              We believe that property development decisions should be based on comprehensive data and 
              analysis, not guesswork. QuickBuildr.ai was founded to democratize access to professional-grade 
              feasibility analysis tools, making them available to everyone from individual investors to 
              large development firms.
            </Typography>
          </Box>

          {/* Story Section */}
          <Grid container spacing={6} alignItems="center" sx={{ my: 8 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Our Story
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  QuickBuildr.ai was born from frustration. As real estate professionals in Massachusetts, 
                  we spent countless hours manually gathering property data, calculating zoning compliance, 
                  and creating feasibility reports. We knew there had to be a better way.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  In 2023, we set out to build the tool we wished existed: a comprehensive platform that 
                  could instantly analyze any property in Massachusetts using real, authenticated data from 
                  official sources.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Today, QuickBuildr.ai helps thousands of real estate professionals make smarter, 
                  data-driven decisions every day. We're proud to be part of Massachusetts' thriving 
                  property development ecosystem.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight={700} gutterBottom>
                  By the Numbers
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h4" fontWeight={600}>5,000+</Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>Properties Analyzed</Typography>
                  
                  <Typography variant="h4" fontWeight={600}>1,200+</Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>Active Users</Typography>
                  
                  <Typography variant="h4" fontWeight={600}>98%</Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>Data Accuracy Rate</Typography>
                  
                  <Typography variant="h4" fontWeight={600}>24/7</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Real-Time Data Updates</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Values Section */}
          <Box sx={{ my: 8 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Our Values
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {values.map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ color: 'primary.main', mb: 2 }}>
                          {value.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {value.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {value.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Coverage Section */}
          <Box sx={{ my: 8 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Where We Serve
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              AI-powered analysis currently available in select Massachusetts towns, with nationwide architectural studies
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Currently Available
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Our AI-powered feasibility analysis is live in:
                  </Typography>
                  {currentCoverage.map((town, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body1" fontWeight={500}>
                        {town}
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="body2" color="primary" sx={{ mt: 2, fontWeight: 600 }}>
                    Full AI analysis with instant results
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Expanding Soon
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Coming to these Massachusetts towns:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {comingSoon.map((town, index) => (
                      <Chip
                        key={index}
                        label={town}
                        variant="outlined"
                        size="small"
                        color="secondary"
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Want your town added? <strong>Call us at 781-528-3339</strong>
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4, p: 3, bgcolor: 'success.main', color: 'white', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Professional Architectural Studies Available Nationwide
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Our $3,000 stamped architectural feasibility studies with official floor plans and renderings 
                are available for <strong>all towns, counties, and states</strong> across the United States.
              </Typography>
              <Typography variant="body1">
                For nationwide architectural studies, call <strong>781-528-3339</strong> or email <strong>sales@quickbuildr.ai</strong>
              </Typography>
            </Box>
          </Box>

          {/* Timeline Section */}
          <Box sx={{ my: 8 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Our Journey
            </Typography>
            <Box sx={{ mt: 4 }}>
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <CheckIcon color="success" sx={{ mr: 2 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" color="primary">
                        {milestone.year}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {milestone.event}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>

          {/* CTA Section */}
          <Box sx={{ mt: 10, p: 6, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Join Us on Our Mission
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Be part of the future of property development analysis
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8 }}>
            © 2025 QuickBuildr.ai. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;